/**
 * A small panel opened from a value in a table that scrolls sideways.
 *
 * A panel positioned inside that box is clipped by it, so the panel is
 * rendered at the document root at a fixed position below the value, kept
 * inside the viewport, and follows the value when the page or the table
 * scrolls. Escape and a click outside close it.
 */

import React from "react";

export interface FloatingPanel {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Closes and returns focus to the value that opened it. */
  close: () => void;
  place: { top: number; left: number } | null;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  panelRef: React.RefObject<HTMLDivElement | null>;
}

export function useFloatingPanel(width: number): FloatingPanel {
  const [open, setOpen] = React.useState(false);
  const [place, setPlace] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const rePlace = React.useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const gutter = 8;
    const left = Math.max(
      gutter,
      Math.min(rect.right - width, window.innerWidth - width - gutter),
    );
    setPlace({ top: rect.bottom + 4, left });
  }, [width]);
  React.useLayoutEffect(() => {
    if (!open) return undefined;
    rePlace();
    window.addEventListener("resize", rePlace);
    document.addEventListener("scroll", rePlace, true);
    return () => {
      window.removeEventListener("resize", rePlace);
      document.removeEventListener("scroll", rePlace, true);
    };
  }, [open, rePlace]);

  const close = React.useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, close]);

  return { open, setOpen, close, place, buttonRef, panelRef };
}
